/* jshint browser:true */
import UiUtils from '../ui-utils';
import ColorUtils from '../color-utils';

/**
 * Management for cards.
 */
export default class Card {
  /**
   * Constructor.
   */
  constructor() {
    this.classNames = {
      overlayPrefix: 'card--overlay-',
      overlayBox: 'card--overlay-box',
      overlayFull: 'card--overlay-full',
      overlayPartial: 'card--overlay-partial',
      content: 'card__content',
      media: 'card__media',
    };

    this.cssCustomProps = {
      opacity: '--card-overlay-opacity',
    };
  }

  /**
   * Initializes cards.
   *
   * @param {string} selector CSS selector to find cards.
   */
  init(selector) {
    const wrappers = document.querySelectorAll(selector);

    if (!wrappers) {
      return;
    }

    wrappers.forEach((x) => {
      this.setup(x);
    });
  }

  /**
   * Sets up an individual card.
   *
   * @param {HTMLElement} wrapper The top-level element of the card.
   */
  async setup(wrapper) {
    const hasOverlay = wrapper.className.includes(
      this.classNames.overlayPrefix,
    );

    if (!hasOverlay) {
      return;
    }

    if (wrapper.dataset.scrimOpacity !== undefined) {
      // Scrim opacity has been prescribed in the HTML.
      this.setScrimOpacity(wrapper, wrapper.dataset.scrimOpacity);
    } else {
      // Determine the best scrim opacity.
      const opacity = await this.getScrimOpacity(wrapper);

      this.setScrimOpacity(wrapper, opacity);
    }
  }

  /**
   * Adjusts the opacity of a card's text protection scrim based on WCAG 2.1 AA
   * contrast guidelines.
   *
   * @param {HTMLElement} wrapper The top-level element of the card.
   * @param {number} defaultOpacity An opacity to use if an optimum opacity can
   *    not be determined.
   * @return {number} The recommended opacity.
   */
  async getScrimOpacity(wrapper, defaultOpacity = 0.9) {
    const contentElement = wrapper.querySelector('.' + this.classNames.content);

    if (contentElement === null) {
      return defaultOpacity;
    }

    // Get color and opacity
    const contentStyle = getComputedStyle(contentElement);
    const afterPseudoStyle = getComputedStyle(contentElement, ':after');
    const textColor = contentStyle.color;
    const bgColor = afterPseudoStyle.backgroundColor;

    const imageElement = wrapper.querySelector(
      '.' + this.classNames.media + ' img',
    );

    if (imageElement === null) {
      return defaultOpacity;
    }

    const opacity = await this.getOptimumScrimOpacity(
      imageElement,
      textColor,
      bgColor,
    );

    return opacity;
  }

  /**
   * Sets the opacity of the text protection scrim pseudo element.
   * @param {HTMLElement} wrapper The top-level element of the card.
   * @param {number} opacity The opacity number (0-1) to use.
   */
  setScrimOpacity(wrapper, opacity) {
    const isContentScrim = wrapper.classList.contains(
      this.classNames.overlayBox,
    );
    const scrimElement = isContentScrim
      ? wrapper.querySelector('.' + this.classNames.content)
      : wrapper.querySelector('.' + this.classNames.media);

    // Do not change opacity if scrim is currently opaque.
    const curOpacity = getComputedStyle(scrimElement).getPropertyValue(
      this.cssCustomProps.opacity,
    );

    if (parseInt(curOpacity) === 1) {
      return;
    }

    // Adjust opacity through the CSS custom property.
    scrimElement.style.setProperty(this.cssCustomProps.opacity, opacity);
  }

  /**
   * Returns a recommended opacity for the content's text protection scrim.
   *
   * @param {HTMLImageElement} imageElement The image element.
   * @param {string} textColorString The RGB color of the content text.
   * @param {string} bgColorString The RGB color of the content background.
   *
   * @return {Promise<number>} The recommended opacity.
   */
  getOptimumScrimOpacity(imageElement, textColorString, bgColorString) {
    const defaultOpacity = 0.9;
    const maxLoadAttempts = 3;
    let loadAttempts = 0;

    // Set up promise and function to get opacity
    const opacityPromise = new Promise((resolve, reject) => {
      const loadEventHandler = () => getOpacityAfterLoaded(imageElement);

      /**
       * Resolves the promise to a recommended opacity.
       *
       * @param {HTMLImageElement} imageElement The image element.
       */
      function getOpacityAfterLoaded(imageElement) {
        // Remove image load event handler
        imageElement.removeEventListener('load', loadEventHandler);
        loadAttempts++;

        // Image hasn't loaded or is a temporary placeholder.
        if (imageElement.naturalHeight <= 1) {
          if (loadAttempts <= maxLoadAttempts) {
            imageElement.addEventListener('load', loadEventHandler);
          }

          return;
        }

        /* We use a small canvas width for our image to blur the image a bit.
           That way, a single light or dark pixel doesn't bump up the opacity
           of the scrim. */
        const canvasWidth = 50;
        const imageData = UiUtils.getImageData(imageElement, canvasWidth);

        if (!imageData) {
          if (loadAttempts <= maxLoadAttempts) {
            imageElement.addEventListener('load', loadEventHandler);
          } else {
            resolve(defaultOpacity);
          }
          return;
        }

        // Find worst contrast between text color and image color
        const textColor = ColorUtils.getObjectFromRgbString(textColorString);
        const dataLength = imageData.data.length;
        let worstContrast = Infinity;
        let worstContrastColor;

        for (let n = 0; n < dataLength; n += 4) {
          const pixelColor = {
            r: imageData.data[n],
            g: imageData.data[n + 1],
            b: imageData.data[n + 2],
          };

          const contrast = ColorUtils.getContrast(textColor, pixelColor);

          if (contrast < worstContrast) {
            worstContrast = contrast;
            worstContrastColor = pixelColor;
          }
        }

        // Try different opacity values to reach an acceptable value
        const minAllowedContrast = 4.5;
        const noOverlayContrast = ColorUtils.getContrast(
          textColor,
          worstContrastColor,
        );

        if (noOverlayContrast >= minAllowedContrast) {
          // No overlay needed
          resolve(0);
          return;
        }

        const bgColor = ColorUtils.getObjectFromRgbString(bgColorString);
        const opacityRange = { low: 0, mid: 0.5, high: 1 };
        const maxAttempts = 6;
        let attempts = 0;

        while (attempts++ < maxAttempts) {
          const scrimContrast = ColorUtils.getTextProtectionScrimContrast(
            worstContrastColor,
            textColor,
            bgColor,
            opacityRange.mid,
          );

          if (scrimContrast === minAllowedContrast) {
            break;
          }

          if (scrimContrast < minAllowedContrast) {
            opacityRange.low = opacityRange.mid;
          } else {
            opacityRange.high = opacityRange.mid;
          }

          opacityRange.mid =
            (opacityRange.high - opacityRange.low) / 2 + opacityRange.low;
        }

        const optimumOpacity = opacityRange.mid;

        resolve(Math.min(optimumOpacity, defaultOpacity));
      }

      // Get opacity after image has loaded
      const imageLoaded =
        imageElement.complete && imageElement.naturalHeight !== 0;

      if (imageLoaded) {
        loadEventHandler();
      } else {
        imageElement.addEventListener('load', loadEventHandler);
      }
    });

    return opacityPromise;
  }
}
